import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert } from '@/components/ui/alert'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON } from '@/lib/api'
import type { EngagementEvent } from '@/lib/engagement-event-registrations'
import type { RegistrationPeopleResponse } from '@/lib/registration-people'
import { RegistrationPeopleTable } from './registration-people-table'

export function EngagementEventPeopleSheet({
  contactId,
  row,
  trigger,
  onClose,
}: {
  contactId: number
  row: EngagementEvent
  trigger: HTMLElement
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('person:asc')
  const params = new URLSearchParams({ organization_id: String(contactId), sort })
  if (search) params.set('q', search)
  const path = `/api/v1/events/${encodeURIComponent(row.event.id)}/registration_comparison/people?${params}`
  const query = useQuery({
    queryKey: ['engagement-event-people', path],
    queryFn: () => getJSON<RegistrationPeopleResponse>(path),
    retry: false,
  })
  const people = query.data?.registration_people
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        className="w-full overflow-hidden sm:max-w-[640px]"
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          ;(event.currentTarget as HTMLElement).focus()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (trigger.isConnected) trigger.focus()
        }}
      >
        <SheetHeader className="shrink-0 pb-0 pr-8">
          <SheetTitle>{row.event.name}</SheetTitle>
          <SheetDescription>{row.description}</SheetDescription>
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <Button variant="link" size="inline" asChild className="text-body-sm">
              <a href={`/events/manage/${encodeURIComponent(row.event.id)}`}>Open event</a>
            </Button>
            <Button variant="link" size="inline" asChild className="text-body-sm">
              <a
                href={`/reports/event-registrations?event_id=${encodeURIComponent(row.event.id)}&organization_id=${contactId}`}
              >
                Open registrations report
              </a>
            </Button>
          </div>
        </SheetHeader>
        <Input
          aria-label="Search people"
          placeholder="Search people"
          className="shrink-0"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div
          role="region"
          aria-label="People list"
          className="min-h-0 min-w-0 flex-1 overflow-auto"
        >
          {query.isFetching ? (
            <p role="status">Loading people…</p>
          ) : query.error ? (
            <Alert>Could not load people. {query.error.message}</Alert>
          ) : people?.empty_message ? (
            <EmptyState variant="quiet" title={people.empty_message} />
          ) : (
            people && (
              <RegistrationPeopleTable
                people={people}
                sort={sort}
                onSort={setSort}
                engagement
                started={row.has_started}
              />
            )
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
